/**
 * Main hook for patient context data
 *
 * Orchestrates fetching and transforming patient context data
 * including medications, diagnoses, allergies, and last encounter.
 */

import { useMemo } from 'react';
import type { Medication } from '@components/Patient/MedicationsDataBlock';
import { useIsTutorialMode } from '../../../context/PatientDataContext';

import {
  useGetPatientModuleDataQuery,
  useGetPatientPreRecordingInfoQuery,
  useGetTreatmentPlanQuery,
} from '../store/api';
import type {
  Allergy,
  Diagnosis,
  LastEncounterData,
  PatientContextData,
  PatientContextLoadingState,
  PatientModuleInfo,
  TreatmentPlanData,
} from '../types';

export interface UsePatientContextReturn extends PatientContextData {
  isLoading: boolean;
  loadingState: PatientContextLoadingState;
  medicationsModuleInfo: PatientModuleInfo | null;
  diagnosesModuleInfo: PatientModuleInfo | null;
}

/**
 * Transform patient module data response to medications array and module info
 */
function transformMedications(data: any): {
  items: Medication[];
  moduleInfo: PatientModuleInfo | null;
} {
  const patientModulesData = data?.patient_data?.patient_module_data;
  if (!patientModulesData) return { items: [], moduleInfo: null };

  // Find medications module - the key is the patient_module_id
  let medicationsModuleId: string | null = null;
  let medicationsModule = null;
  for (const [moduleId, moduleData] of Object.entries(patientModulesData)) {
    const mod = moduleData as any;
    if (mod.name?.toLowerCase() === 'medications' && mod.type === 'table') {
      medicationsModuleId = moduleId;
      medicationsModule = mod;
      break;
    }
  }

  if (!medicationsModule?.current_data || !medicationsModuleId) {
    return { items: [], moduleInfo: null };
  }

  const dataKey = Object.keys(medicationsModule.current_data)[0];
  const tableData = medicationsModule.current_data[dataKey] || [];

  if (!Array.isArray(tableData) || tableData.length === 0) {
    return {
      items: [],
      moduleInfo: { patient_module_id: medicationsModuleId, dataKey },
    };
  }

  const items = tableData.map((row: any) => ({
    medication_name: row.name || row.medication_name || '',
    generic_name: row.generic_name || null,
    formulation: row.formulation || null,
    strength: row.strength || null,
    route_of_administration: row.route_of_administration || null,
    dosage_amount: row.dosage_amount || null,
    frequency: row.frequency || null,
    timing: row.timing || null,
    start_date: row.start_date || null,
    end_date: row.end_date || null,
    indication: row.indication || row.notes || null,
    isActive: row.active !== false && !row.end_date,
  }));

  return {
    items,
    moduleInfo: { patient_module_id: medicationsModuleId, dataKey },
  };
}

/**
 * Transform patient module data response to diagnoses array and module info
 */
function transformDiagnoses(data: any): {
  items: Diagnosis[];
  moduleInfo: PatientModuleInfo | null;
} {
  const patientModulesData = data?.patient_data?.patient_module_data;
  if (!patientModulesData) return { items: [], moduleInfo: null };

  // Find diagnoses module - the key is the patient_module_id
  let diagnosesModuleId: string | null = null;
  let diagnosesModule = null;
  for (const [moduleId, moduleData] of Object.entries(patientModulesData)) {
    const mod = moduleData as any;
    if (mod.name?.toLowerCase() === 'diagnoses' && mod.type === 'table') {
      diagnosesModuleId = moduleId;
      diagnosesModule = mod;
      break;
    }
  }

  if (!diagnosesModule?.current_data || !diagnosesModuleId) {
    return { items: [], moduleInfo: null };
  }

  const dataKey = Object.keys(diagnosesModule.current_data)[0];
  const tableData = diagnosesModule.current_data[dataKey] || [];

  if (!Array.isArray(tableData) || tableData.length === 0) {
    return {
      items: [],
      moduleInfo: { patient_module_id: diagnosesModuleId, dataKey },
    };
  }

  const items = tableData.map((row: any) => ({
    diagnosis_name: row.name || row.diagnosis_name || '',
    code: row.code || null,
    start_date: row.start_date || null,
    end_date: row.end_date || null,
    isActive: row.active !== false && !row.end_date,
  }));

  return {
    items,
    moduleInfo: { patient_module_id: diagnosesModuleId, dataKey },
  };
}

/**
 * Transform patient module data response to allergies array
 */
function transformAllergies(data: any): Allergy[] {
  const patientModulesData = data?.patient_data?.patient_module_data;
  if (!patientModulesData) return [];

  // Find allergies module
  let allergiesModule = null;
  for (const [, moduleData] of Object.entries(patientModulesData)) {
    const mod = moduleData as any;
    if (mod.name?.toLowerCase() === 'allergies' && mod.type === 'table') {
      allergiesModule = mod;
      break;
    }
  }

  if (!allergiesModule?.current_data) return [];

  const dataKey = Object.keys(allergiesModule.current_data)[0];
  const tableData = allergiesModule.current_data[dataKey] || [];

  if (!Array.isArray(tableData) || tableData.length === 0) return [];

  return tableData.map((row: any) => ({
    name: row.name || null,
    allergy_name: row.allergy_name || row.name || null,
    reaction: row.reaction || null,
    severity: row.severity || null,
    start_date: row.start_date || null,
    end_date: row.end_date || null,
    isActive: row.active !== false && !row.end_date,
  }));
}

/**
 * Transform pre-recording info response to LastEncounterData
 */
function transformLastEncounter(data: any): LastEncounterData | null {
  if (!data) return null;

  return {
    past_visit_summary: data.past_visit_summary,
    latest_encounter: data.patient?.latest_encounter,
  };
}

/**
 * Hook for fetching and transforming patient context data
 *
 * @param patientId - Patient ID to fetch context for (null skips fetching)
 * @returns Patient context data with loading states
 */
export function usePatientContext(patientId: string | null): UsePatientContextReturn {
  const isTutorialMode = useIsTutorialMode();

  // Fetch patient module data (medications, diagnoses, allergies)
  const {
    data: moduleData,
    isLoading: isLoadingModuleData,
    error: moduleDataError,
  } = useGetPatientModuleDataQuery(
    { patientId: patientId! },
    { skip: !patientId || isTutorialMode },
  );

  // Fetch pre-recording info (last encounter)
  const {
    data: preRecordingInfo,
    isLoading: isLoadingPreRecordingInfo,
    error: preRecordingError,
  } = useGetPatientPreRecordingInfoQuery(
    { patientId: patientId! },
    { skip: !patientId || isTutorialMode },
  );

  // Fetch treatment plan
  const {
    data: treatmentPlanData,
    isLoading: isLoadingTreatmentPlan,
    error: treatmentPlanError,
  } = useGetTreatmentPlanQuery(
    { patientId: patientId! },
    { skip: !patientId || isTutorialMode },
  );

  // Transform data
  const medicationsResult = useMemo(() => {
    if (!moduleData || moduleDataError) return { items: [], moduleInfo: null };
    return transformMedications(moduleData);
  }, [moduleData, moduleDataError]);

  const diagnosesResult = useMemo(() => {
    if (!moduleData || moduleDataError) return { items: [], moduleInfo: null };
    return transformDiagnoses(moduleData);
  }, [moduleData, moduleDataError]);

  const medications = medicationsResult.items;
  const diagnoses = diagnosesResult.items;
  const medicationsModuleInfo = medicationsResult.moduleInfo;
  const diagnosesModuleInfo = diagnosesResult.moduleInfo;

  const allergies = useMemo(() => {
    if (!moduleData || moduleDataError) return [];
    return transformAllergies(moduleData);
  }, [moduleData, moduleDataError]);

  const lastEncounter = useMemo(() => {
    if (!preRecordingInfo || preRecordingError) return null;
    return transformLastEncounter(preRecordingInfo);
  }, [preRecordingInfo, preRecordingError]);

  const treatmentPlan = useMemo((): TreatmentPlanData | null => {
    if (!treatmentPlanData || treatmentPlanError) return null;
    return {
      markdown: treatmentPlanData.markdown_content || '',
      treatment_plan_id: treatmentPlanData.treatment_plan_id || null,
    };
  }, [treatmentPlanData, treatmentPlanError]);

  const loadingState: PatientContextLoadingState = useMemo(
    () => ({
      patient: false, // Patient is passed in, not fetched here
      medications: isLoadingModuleData,
      diagnoses: isLoadingModuleData,
      allergies: isLoadingModuleData,
      lastEncounter: isLoadingPreRecordingInfo,
      treatmentPlan: isLoadingTreatmentPlan,
    }),
    [isLoadingModuleData, isLoadingPreRecordingInfo, isLoadingTreatmentPlan],
  );

  const isLoading = isLoadingModuleData || isLoadingPreRecordingInfo || isLoadingTreatmentPlan;

  return {
    patient: null, // Patient should be passed separately
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    treatmentPlan,
    isLoading,
    loadingState,
    medicationsModuleInfo,
    diagnosesModuleInfo,
  };
}

